/**
 * Key Findings derivation — RTB-3 (Report TOC-Body Consistency, 2026-05-18).
 *
 * Live-prod 2026-05-18 (Redhead Feasibility v4.0): the report TOC listed
 * `2. Key Findings § 2` but the body had NO `<h2>Key Findings</h2>`. The
 * inline render guard in `SiteRagReportView` silently dropped the section
 * when `structuredFindings` was empty or undefined. The fix has two halves:
 *
 *   - RTB-2 (in `SiteRagReportView`) — always emit the section wrapper
 *     and inside render either real findings or a placeholder.
 *   - RTB-3 (this file) — when the LLM didn't emit direct `structuredFindings`
 *     but CSE-derived `report.claims[]` is present, derive a top-N findings
 *     list from the structured claims as a graceful fallback.
 *
 * The derivation is intentionally conservative:
 *   - Cap at 5 entries so the section reads as a summary, not an info dump.
 *   - Require claims to carry at least one source (`sources.length >= 1`).
 *     The pack's spec asks for a "confidence ≥ 0.8" gate, but `ReportClaim`
 *     in `src/types/citation.ts` does NOT carry a numeric confidence — it
 *     carries `text`, `sources`, and optional `sectionId`. The closest
 *     stand-in for confidence we have is the count of distinct lineage
 *     sources, so the floor here is `sources.length >= 1` and the ranking
 *     is by source count, then by text length (longer = richer claim).
 *   - Prefer claims with `sectionId === 'keyFindings'` when present so the
 *     model's own grouping is respected when it exists.
 *   - Output the same `{ title, detail, source, impact }` shape the inline
 *     renderer already iterates so the callsite can drop the derived
 *     findings straight into the existing JSX.
 *
 * Each derived finding carries `derivedFromClaims = true` so the renderer
 * can stamp a small "Auto-derived from structured claims" note rather than
 * passing them off as model-authored narrative.
 */
import type { SiteRagReport } from '../types/siteRag';
import type { ReportClaim } from '../types/citation';

/**
 * The viewer's existing structured-findings entry shape (mirrors the
 * inline render on `SiteRagReportView.tsx` line ~2005). Exported so the
 * callsite and tests share one type.
 */
export interface DerivedFinding {
  title: string;
  detail: string;
  source: string;
  /** 'positive' | 'negative' | 'neutral' — drives the dot colour in the
   *  inline render. Derived findings always render as 'neutral' (slate
   *  dot) because we don't have impact metadata on a `ReportClaim`. */
  impact: 'positive' | 'negative' | 'neutral';
  /** RTB-3 flag — the renderer surfaces a small "Auto-derived" note
   *  next to the section heading when at least one entry has this set. */
  derivedFromClaims: true;
}

/**
 * Reasons the inline Key Findings section is empty. Surfaced to the
 * `SectionPlaceholder` so the placeholder copy can adapt:
 *   - 'extractor-empty' → "The AI didn't surface key findings for this
 *      report. Run a full rerun to regenerate this section."
 *   - 'data-too-thin'   → "Site evidence is too thin to summarize.
 *      Upload more documents or run Refresh Evidence."
 *   - 'quarantined'     → forwards to the SNT-3 quarantine banner; the
 *      copy promises the section is being reviewed.
 */
export type EmptyKeyFindingsReason =
  | 'extractor-empty'
  | 'data-too-thin'
  | 'quarantined';

/**
 * Diagnose why `structuredFindings` is empty so the placeholder copy
 * can adapt. Returns:
 *   - 'quarantined' when `report.sectionQuarantine?.keyFindings` is set
 *     (SNT-3 quarantine flag — the section was flagged degraded
 *     upstream and the SNT banner already explains why).
 *   - 'data-too-thin' when `report.claims` is also empty AND the report
 *     has no references (the upstream extractor had nothing to chew on).
 *   - 'extractor-empty' otherwise (the model just didn't emit findings).
 *
 * The function is pure / read-only so it's safe to call during render.
 */
export function diagnoseEmptyKeyFindings(report: SiteRagReport): EmptyKeyFindingsReason {
  // Quarantine state lives on a free-form record indexed by sectionId.
  // The field is optional on SiteRagReport; cast through `unknown` so
  // strict mode doesn't complain when the schema doesn't list it.
  const quarantineMap = (report as unknown as {
    sectionQuarantine?: Record<string, unknown>;
  }).sectionQuarantine;
  if (quarantineMap && (quarantineMap.projectIntentOverview != null || quarantineMap.keyFindings != null)) {
    return 'quarantined';
  }

  const hasClaims = Array.isArray(report.claims) && report.claims.length > 0;
  const hasReferences = Array.isArray(report.references) && report.references.length > 0;
  if (!hasClaims && !hasReferences) return 'data-too-thin';
  return 'extractor-empty';
}

/**
 * Turn a snake_or-camelCased field path into a Title Case label.
 * Used when a `ReportClaim` doesn't carry a custom title — we synthesize
 * one from the leading sentence or fallback to the section id.
 *
 * Inlined here (rather than imported) because no general-purpose
 * humanize utility currently lives in src/lib and pulling one in would
 * expand scope.
 */
function humanize(input: string): string {
  if (!input) return '';
  // Insert a space before each capital, then split on non-alphanumeric.
  const spaced = input.replace(/([A-Z])/g, ' $1').replace(/[_\-.]+/g, ' ');
  return spaced
    .split(' ')
    .map(w => w.trim())
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Extract a short title from the first sentence (≤ 80 chars) of a claim's
 * text. Falls back to the humanized sectionId when text is empty.
 *
 * KFH-1 (2026-05-19) — when the candidate ≤80 char window happens to
 * land INSIDE a bracketed token (e.g. `[Ref 18]` or a residual
 * `[Project Intent Workload Profile]` placeholder), the previous
 * implementation cut mid-token and left a mangled
 * `"... construction w[Ref 18]"` style title. Combined with the
 * renderer's "title + detail" two-line shape for derived findings, the
 * truncated title looked like a separate (broken) bullet next to the
 * full sentence detail — the user-visible "double render" defect on
 * Sickel Feasibility v3.0.
 *
 * The fix: when the natural ≤80 char window straddles a `[...]` opener,
 * back off to the last word/sentence boundary BEFORE the opener so the
 * title ends cleanly. We never extend past 80 chars (the title is meant
 * to be a one-line lead-in); if no clean break exists in the first
 * 80 chars, we fall through to the whole text up to the first sentence
 * terminator.
 */
function titleFromClaim(claim: ReportClaim): string {
  const text = (claim.text ?? '').trim();
  if (text) {
    // Split on the first sentence-terminator, fall back to first 80 chars.
    const m = text.match(/^[^.!?]{1,80}/);
    let head = (m ? m[0] : text.slice(0, 80)).trim();
    // KFH-1 — if the cut landed INSIDE a `[...]` token (unclosed bracket
    // in the candidate head), back off to the last whitespace before the
    // opener so the title never reads "... morator[Ref 1]" or "...
    // site in[Project Intent Workload Profile]".
    const openIdx = head.lastIndexOf('[');
    const closeIdx = head.lastIndexOf(']');
    if (openIdx > -1 && openIdx > closeIdx) {
      const backoff = head.slice(0, openIdx).replace(/[\s,;:]+$/, '');
      // Only adopt the back-off if it leaves at least one word —
      // otherwise we'd produce an empty title and fall through to the
      // sectionId humanization below.
      if (backoff.length > 0) head = backoff;
    }
    return head.length === 0 ? humanize(claim.sectionId ?? 'finding') : head;
  }
  return humanize(claim.sectionId ?? 'finding');
}

/**
 * Render a claim's `sources` array into a compact source label. The
 * inline render's `[{f.source}]` decoration expects a short token, so
 * pick the leading source and humanize it.
 */
function formatSourceLabel(claim: ReportClaim): string {
  const first = claim.sources[0];
  if (!first) return 'derived';
  // ref:3 → "Ref 3" ; doc:abc → "Doc abc" ; sed:climate → "Climate"
  const parts = first.split(':');
  if (parts.length < 2) return first;
  const [prefix, payload] = parts;
  if (prefix === 'ref' || prefix === 'doc' || prefix === 'baseline' || prefix === 'provider') {
    return `${prefix.charAt(0).toUpperCase()}${prefix.slice(1)} ${payload}`;
  }
  return humanize(payload) || first;
}

/**
 * Derive up to 5 `DerivedFinding`s from `report.claims[]` ranked by
 * source-count then text length. Returns `[]` when no eligible claims
 * exist — callers should fall through to the placeholder copy.
 *
 * Ranking notes:
 *   - Claims with `sectionId === 'keyFindings'` get a +10 boost so the
 *     model's own grouping is honored when present.
 *   - Claims with at least one ref:/doc:/sed: source are preferred over
 *     bare claims with no sources (those are dropped entirely).
 *   - Stable tie-break by original array index so the output is
 *     deterministic across renders.
 *
 * The output is render-only: this function does NOT mutate the report
 * doc, so the derivation re-runs each render and archived reports
 * continue to read as they did (RTB R5 mitigation).
 */
export function deriveKeyFindingsFromClaims(report: SiteRagReport): DerivedFinding[] {
  if (!Array.isArray(report.claims) || report.claims.length === 0) return [];

  // Pair each eligible claim with a ranking score + its original index
  // so we can sort stably without losing position information.
  const ranked = report.claims
    .map((claim, idx) => ({ claim, idx }))
    .filter(({ claim }) => {
      // Drop claims with no sources — these are the "uncited" entries
      // we don't want to surface as if they were verified findings.
      return Array.isArray(claim.sources) && claim.sources.length >= 1;
    })
    .map(({ claim, idx }) => {
      const sourceCount = claim.sources.length;
      const sectionBoost =
        claim.sectionId === 'projectIntentOverview' || claim.sectionId === 'keyFindings' ? 10 : 0;
      const textLen = (claim.text ?? '').length;
      // Score: dominated by section boost, then source count, then a
      // small text-length signal so longer well-formed claims edge out
      // single-word fragments.
      const score = sectionBoost + sourceCount * 2 + Math.min(textLen, 200) / 100;
      return { claim, idx, score };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.idx - b.idx;
    })
    .slice(0, 5);

  return ranked.map(({ claim }) => ({
    title: titleFromClaim(claim),
    detail: claim.text ?? '',
    source: formatSourceLabel(claim),
    impact: 'neutral',
    derivedFromClaims: true,
  }));
}
